// Ask Sophie streaming client — POST + SSE framing, read through fetch.
//
// Wire contract: tikcare_rag docs/STREAMING-DESIGN.md §3.
//   status*  ->  delta*  ->  report?  ->  (done | error)
//
// THE RULE THIS FILE EXISTS TO ENFORCE: `done` is the only completion signal.
// A stream that stops — the reader resolving done:true, or throwing — without a
// `done` event is a FAILURE, tracked as an explicit `sawDone` boolean rather
// than inferred from the absence of an error. `done.data` is canonical and is
// the same shape as today's /askanything 200 body; the deltas are a display
// optimisation, so the caller replaces the bubble text with `done.answer` and
// persists only that. Nothing incomplete is ever written to textmessages.
import { readSSE } from './sseFetch.js';

export const OUTCOME = {
  DONE: 'done',          // terminal `done` event: the answer is complete
  ERROR: 'error',        // terminal `error` event on an already-200 stream
  TRUNCATED: 'truncated' // the stream stopped without either. Never persist.
};

// The clinical rule, named so the call site reads as what it is: an answer is
// written to textmessages only when the `done` event delivered it whole. That
// table has no status column and is re-rendered later, out of context, by
// fetchChatMessages — a fragment saved today reads as Sophie's finished opinion
// next week. Not saving costs one re-ask; saving costs a wrong record.
export function shouldPersistAnswer(result) {
  return !!result && result.outcome === 'done';
}

// The same rule facing the other way. A bubble that is still streaming, still a
// placeholder, or was cut off is not a turn that happened, so it must not be
// sent back as chat_history on the next question. Refusing to save a fragment
// and then feeding that same fragment to the model would be the identical
// mistake with a faster fuse: the follow-up answer gets built on top of a
// half-sentence that Sophie is told she finished saying.
export function isCompletedTurn(message) {
  return !!message && !message.isProcessing && !message.isStreaming && !message.isTruncated;
}

// Whether a bubble should show the "N sources" line that opens the sources
// dialog. Built on isCompletedTurn for the same reason: a fragment's citations
// describe an answer nobody has, and a doctor who sees "3 sources" under a
// cut-off bubble reads it as evidence that the fragment is documented.
//
// The answer is also the doctor's own question when senderid is not the
// assistant; only Sophie's answers carry sources.
export function hasVisibleSources(message) {
  return (
    isCompletedTurn(message) &&
    message.senderid === 'ai-assistant' &&
    Array.isArray(message.sources) &&
    message.sources.length > 0
  );
}

// "1 source", not "1 sources". Singular matters here because a lone citation is
// the common case for a short factual answer.
export function sourcesLabel(count) {
  return count === 1 ? '1 source' : `${count} sources`;
}

// A frame whose JSON does not parse is worthless; for a terminal frame that
// means we cannot trust it as a completion, so it degrades to a truncation.
function parseData(raw) {
  try {
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
}

/**
 * @returns {Promise<{outcome, data?, partial, reason?, error?}>}
 *   - OUTCOME.DONE: `data` is the canonical response body.
 *   - OUTCOME.ERROR / OUTCOME.TRUNCATED: `partial` is whatever text arrived.
 * Throws only for pre-stream failures (network error, non-2xx) — those are
 * today's total-failure case and belong in the caller's existing catch.
 */
export async function streamAskSophie({ url, params, headers, fetchImpl, onStage, onDelta, onReport }) {
  const doFetch = fetchImpl || ((...args) => fetch(...args));

  const response = await doFetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(params),
  });

  // Pre-stream failures are real status codes (400 missing question, 401 from
  // the JWT boundary); post-stream failures arrive as `event: error` on a 200.
  // So the status check has to happen before the first read.
  if (!response.ok) {
    throw new Error(`Ask Sophie stream failed: HTTP ${response.status}`);
  }

  let sawDone = false;
  let terminal = null;
  let partial = '';

  try {
    for await (const { event, data } of readSSE(response)) {
      if (event === 'status') {
        const payload = parseData(data);
        if (payload && payload.stage && onStage) onStage(payload.stage, payload.label);
      } else if (event === 'delta') {
        const payload = parseData(data);
        if (payload && typeof payload.text === 'string') {
          partial += payload.text; // append verbatim — no trimming
          if (onDelta) onDelta(partial, payload.text);
        }
      } else if (event === 'report') {
        const payload = parseData(data);
        if (payload && onReport) onReport(payload);
      } else if (event === 'done') {
        const payload = parseData(data);
        if (payload && typeof payload.answer === 'string') {
          sawDone = true;
          terminal = { outcome: OUTCOME.DONE, data: payload, partial };
        } else {
          terminal = { outcome: OUTCOME.TRUNCATED, partial, reason: 'unparseable_done' };
        }
        break;
      } else if (event === 'error') {
        terminal = { outcome: OUTCOME.ERROR, data: parseData(data), partial };
        break;
      }
      // Unknown event types are ignored so a backend can add one without
      // breaking a deployed frontend.
    }
  } catch (error) {
    // A read that throws mid-answer is a truncation, not a transport bug to
    // rethrow: the partial text is already on the doctor's screen and has to be
    // marked as cut off, not replaced by a generic error bubble.
    if (!sawDone) return { outcome: OUTCOME.TRUNCATED, partial, reason: 'read_failed', error };
  }

  // The stream ended cleanly but said nothing terminal — still a failure.
  if (!sawDone && !terminal) {
    return { outcome: OUTCOME.TRUNCATED, partial, reason: 'stream_ended' };
  }

  return terminal;
}
